/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  DefaultValue,
  STATUS_MAX_OCCUPANCY_EXCEEDED,
  STATUS_NOT_AVAILABLE,
} from '../../organisms/OSearchResults/index.constant';
import { getPriceByDecimalPoint } from '../../organisms/OSearchResults/lib/helper';
import { RATES_DATA_FOR_SELECTED_PROPERTY, SESSION_DATA_VALUES } from '../constants';
import { createDecimalValue } from './helpers';


/**
 * Formats mandatory fee amount for display, matching SERP/HQV convention (Math.floor).
 */
export const formatMandatoryFeeForDisplay = (amount: number, decimalPoint: number): number => {
  return Math.floor(createDecimalValue(amount, decimalPoint));
};

//function to set rates data for selected property on searchResults Zustand Store
export const fetchAndSetSelectedPropertyData = (
  setShopSearchResultsState: any,
  setSearchResultsState: any,
  ratesData: any,
  propertyID: string,
  isFlexibleDateSearch: boolean,
  continueCtaProperty: string
) => {
  // Find the matching edge for the selected property
  // Use case-insensitive comparison to handle potential case mismatches
  let matchedEdge: any = null;
  const normalizedPropertyID = propertyID?.toUpperCase();
  ratesData?.edges?.forEach((edge: any) => {
    const propertyIdFromNode = edge?.node?.property ? edge?.node?.property?.id : edge?.node?.id;
    const normalizedEdgePropertyId = propertyIdFromNode?.toUpperCase();
    if (normalizedEdgePropertyId === normalizedPropertyID) {
      matchedEdge = edge;
    }
  });

  // Always update the store with the matched edge (or null if not found)
  // This ensures stale rate data from a previous property is cleared
  setShopSearchResultsState([RATES_DATA_FOR_SELECTED_PROPERTY], {
    [RATES_DATA_FOR_SELECTED_PROPERTY]: { [RATES_DATA_FOR_SELECTED_PROPERTY]: matchedEdge },
  });
  setSearchResultsState([RATES_DATA_FOR_SELECTED_PROPERTY], {
    [RATES_DATA_FOR_SELECTED_PROPERTY]: { [RATES_DATA_FOR_SELECTED_PROPERTY]: matchedEdge },
  });

  setSearchResultsState([SESSION_DATA_VALUES], {
    [SESSION_DATA_VALUES]: {
      [SESSION_DATA_VALUES]: {
        isFlexibleSearch: isFlexibleDateSearch,
        recentlyViewedPropertyCode: continueCtaProperty,
      },
    },
  });
  setShopSearchResultsState([SESSION_DATA_VALUES], {
    [SESSION_DATA_VALUES]: {
      [SESSION_DATA_VALUES]: {
        isFlexibleSearch: isFlexibleDateSearch,
        recentlyViewedPropertyCode: continueCtaProperty,
      },
    },
  });
};

//function to get price info from selected node
export const getPriceInfo = (rateDetails: any, isLocaleAllowed: boolean, isFlexibleDateSearch: boolean) => {
  if (rateDetails?.amountPlusMandatoryFees && isLocaleAllowed) {
    return isFlexibleDateSearch ? null : rateDetails?.amountPlusMandatoryFees?.amount;
  } else {
    return isFlexibleDateSearch ? null : rateDetails?.amount?.amount;
  }
};

//function to get total price from selected node
export const getTotalPrice = (rateDetails: any, isFlexibleDateSearch: boolean, isTaxesAndFeesEnabled: boolean) => {
  return isFlexibleDateSearch ? null : isTaxesAndFeesEnabled ? rateDetails?.totalAmount : null;
};

//function to check if mandatory fee is available for selected node
export const mandatoryFeeAvailable = (rateDetails: any, isLocaleAllowed: boolean, isFlexibleDateSearch: boolean) => {
  if (!isLocaleAllowed) {
    return false;
  } else {
    return isFlexibleDateSearch
      ? null
      : rateDetails?.mandatoryFees
      ? rateDetails?.mandatoryFees?.amount !== 0
        ? true
        : false
      : false;
  }
};

//function to set mandatory fee title
export const constructMandatoryFeeTitle = (
  isResort: boolean | string,
  isLACountyFee: boolean,
  isMarketingFee: boolean,
  isDestinationFee: boolean,
  jsonContent: any
) => {
  let title = '';

  title = isResort
    ? jsonContent?.resortLabel + ' ' + jsonContent?.feeLabel
    : isLACountyFee
    ? jsonContent?.surchargeTooltipHeadingLabel ?? ''
    : isMarketingFee
    ? jsonContent?.tourismMarketingFeeLabel
    : isDestinationFee
    ? jsonContent?.destinationAmenityLabel + ' ' + jsonContent?.feeLabel
    : '';

  return title;
};

//function to determine if rates are present for particular property
export const determineIfRatesPresent = (pointsRateBlock: any, priceRateBlock: any, eeoRateBlock?: any) => {
  return (
    (pointsRateBlock?.rateModes && Object.keys(pointsRateBlock?.rateModes).length > 0) ||
    (priceRateBlock?.amount && Object.keys(priceRateBlock?.amount).length > 0) ||
    (eeoRateBlock?.amount && Object.keys(eeoRateBlock?.amount).length) > 0
  );
};

//function to determine of hotel code = unavailable
export const getHotelCodeUnavailable = (
  currRateNode: any,
  isFlexibleDatesOptionSelected: any,
  selectedRateBlockForPoints: any
) => {
  if (
    !isFlexibleDatesOptionSelected &&
    currRateNode?.status?.code === STATUS_NOT_AVAILABLE &&
    selectedRateBlockForPoints?.status?.code === STATUS_NOT_AVAILABLE
  ) {
    return true;
  } else {
    return false;
  }
};

//function to determing if to show sold out or rate unavailable
export const getSoldOutText = (isSpecialRateSearch: boolean, labels: any, showSoldOut: boolean) => {
  if (isSpecialRateSearch) {
    if (showSoldOut) {
      return labels?.soldOutLabel;
    } else {
      return labels?.rateUnavailableLabel;
    }
  } else {
    return labels?.soldOutLabel;
  }
};

//function to construct tool tip data
export const constructToolTipData = (
  isResort: boolean | string,
  isLACountyFee: boolean,
  isMarketingFee: boolean,
  isDestinationFee: boolean,
  labels: any
) => {
  let toolTipTitle = '';
  let mobileTooltipMessage = '';

  if (isResort) {
    toolTipTitle = labels?.dailyResortFeeLabel ? labels?.dailyResortFeeLabel : '';
    mobileTooltipMessage = labels?.resortLabel ? labels?.resortLabel : '';
    mobileTooltipMessage = mobileTooltipMessage + ' ' + labels?.feeLabel;
  } else if (isLACountyFee) {
    toolTipTitle = labels?.surchargeTooltipHeadingLabel
      ? labels?.surchargeTooltipHeadingLabel
      : 'Surcharge For Ordinance Cost';
    mobileTooltipMessage = labels?.surchargeCostsSearchLabel
      ? labels?.surchargeCostsSearchLabel
      : 'Surcharge For Ordinance Cost';
  } else if (isMarketingFee) {
    toolTipTitle = labels?.dailyTourismMarketingFeeLabel ? labels?.dailyTourismMarketingFeeLabel : '';
    mobileTooltipMessage = labels?.tourismMarketingFeeLabel ? labels?.tourismMarketingFeeLabel : '';
  } else if (isDestinationFee) {
    toolTipTitle = labels?.dailyDestinationFeeLabel ? labels?.dailyDestinationFeeLabel : '';
    mobileTooltipMessage = labels?.destinationAmenityLabel ? labels?.destinationAmenityLabel : '';
    mobileTooltipMessage = mobileTooltipMessage + ' ' + labels?.feeLabel;
  } else {
    toolTipTitle = '';
    mobileTooltipMessage = '';
  }

  return { toolTipTitle, mobileTooltipMessage };
};

//function to get correct tooltip data
export const getToolTipData = (propertyData: any) => {
  return (
    propertyData?.basicInformation?.descriptions &&
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    propertyData?.basicInformation?.descriptions.filter((item: any) => {
      if (
        item?.type?.enumCode === 'RESORT_FEE_DESCRIPTION' ||
        item?.type?.enumCode === 'DESTINATION_FEE_DESCRIPTION' ||
        item?.type?.enumCode === 'SURCHARGE_ORDINANCE_COST_DESCRIPTION' ||
        item?.type?.enumCode === 'TOURISM_MARKETING_FEE_DESCRIPTION' ||
        item?.text?.toLowerCase().includes('a resort fee of') ||
        item?.text?.toLowerCase().includes('a daily destination fee of')
      ) {
        return item;
      } else {
        return false;
      }
    })
  );
};

//function to determine if hotel is unavailable or max occupied
export const hotelUnavailableOrMaxOccupied = (
  rateNode: any,
  pointsRateBlock: any,
  eeoRateBlock: any,
  isFlexibleDateSearch: boolean,
  openingDate: any
) => {
  let isHotelMaxOccupancyExceeded = false;
  let isHotelUnavailable = false;

  if (
    !isFlexibleDateSearch &&
    (rateNode?.status?.code === STATUS_MAX_OCCUPANCY_EXCEEDED ||
      pointsRateBlock?.status?.code === STATUS_MAX_OCCUPANCY_EXCEEDED ||
      eeoRateBlock?.status?.code === STATUS_MAX_OCCUPANCY_EXCEEDED)
  ) {
    isHotelMaxOccupancyExceeded = true;
  } else if (
    !isFlexibleDateSearch &&
    !(rateNode?.rateModes?.lowestAverageRate?.amount || pointsRateBlock?.rateModes?.pointsPerUnit?.points) &&
    openingDate
  ) {
    isHotelUnavailable = true;
  }
  return { isHotelMaxOccupancyExceeded, isHotelUnavailable };
};

//function to identify type of mandatory fee
export const identifyTypeOfMandatoryFee = (propertyData: any) => {
  let isResort: boolean | string = propertyData?.basicInformation?.resort;
  let isLACountyFee: boolean = false;
  let isMarketingFee: boolean = false;
  let isDestinationFee: boolean = false;

  //to display resort or destination fee label after checking description type
  if (propertyData?.descriptions) {
    propertyData?.descriptions.forEach((item: { type: { code: string; enumCode: string } }) => {
      if (item?.type?.enumCode === 'RESORT_FEE_DESCRIPTION') {
        isResort = true;
      } else if (item?.type?.enumCode === 'SURCHARGE_ORDINANCE_COST_DESCRIPTION') {
        isLACountyFee = true;
      } else if (item?.type?.enumCode === 'DESTINATION_FEE_DESCRIPTION') {
        isDestinationFee = true;
      } else if (item?.type?.enumCode === 'TOURISM_MARKETING_FEE_DESCRIPTION') {
        isMarketingFee = true;
      }
    });
  }
  return { isResort, isLACountyFee, isMarketingFee, isDestinationFee };
};

export const mandatoryFeeDescriptionForPoints = (
  mandatoryFeeRates: any,
  searchQueryState: any,
  currentLocale: any,
  propertyCardData: any,
  labels: any,
  currentCurrency: any,
  HQVRateConversionArr: any,
  HQVSelectedCurrency: any,
  pointsSaverFlag: any,
  currentViewIsTab: boolean,
  HQVRateBlockFlag: any
) => {
  const displayRates = getPriceByDecimalPoint(
    mandatoryFeeRates?.amount,
    mandatoryFeeRates?.decimalPoint,
    searchQueryState,
    currentLocale,
    mandatoryFeeRates?.currency,
    HQVRateConversionArr,
    HQVSelectedCurrency
  );
  const { isResort, isLACountyFee, isMarketingFee, isDestinationFee } = identifyTypeOfMandatoryFee(
    propertyCardData?.node?.property?.basicInformation
  );
  const displayLabel = isResort
    ? labels?.resortLabel
    : isLACountyFee
    ? labels?.surchargeCostsSearchLabel
    : isMarketingFee
    ? labels?.tourismMarketingFeeLabel
    : isDestinationFee
    ? labels?.destinationAmenityLabel
    : '';
  return `+ ${displayRates} ${currentCurrency !== DefaultValue ? currentCurrency : mandatoryFeeRates?.currency} ${
    labels?.dailyLabel
  } ${displayLabel}${pointsSaverFlag && currentViewIsTab && !HQVRateBlockFlag ? ' ' + labels?.feeLabel : ''}`;
};
